import { create } from 'zustand'

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
  tier: 'free' | 'basic' | 'premium' | 'enterprise'
}

interface AuthState {
  user: User | null
  points: number
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean

  // Actions
  setUser: (user: User | null) => void
  setPoints: (points: number) => void
  login: (user: User, points: number) => void
  logout: () => void
  updatePoints: (delta: number) => void
  setLoading: (loading: boolean) => void
  setInitialized: (initialized: boolean) => void
  checkSession: () => Promise<boolean>
}

// API base URL
const API_BASE = import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost' ? '/api/v1' : 'https://aistudio-api.wizice100.workers.dev/api/v1')

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  points: 0,
  isAuthenticated: false,
  isLoading: true,
  isInitialized: false,

  setUser: (user) => set({ user, isAuthenticated: !!user }),
  setPoints: (points) => set({ points }),
  setLoading: (isLoading) => set({ isLoading }),
  setInitialized: (isInitialized) => set({ isInitialized }),

  login: (user, points) => set({
    user,
    points,
    isAuthenticated: true,
    isLoading: false,
  }),

  logout: async () => {
    try {
      // 서버에 로그아웃 요청 (쿠키 삭제)
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (e) {
      console.error('Logout error:', e)
    }
    set({
      user: null,
      points: 0,
      isAuthenticated: false,
    })
  },

  updatePoints: (delta) => set((state) => ({
    points: Math.max(0, state.points + delta),
  })),

  // 서버 세션 확인
  checkSession: async () => {
    const { isInitialized } = get()

    // 이미 초기화된 경우 현재 인증 상태 반환
    if (isInitialized) {
      return get().isAuthenticated
    }

    set({ isLoading: true })

    try {
      const response = await fetch(`${API_BASE}/users/me`, {
        credentials: 'include',
      })

      if (response.ok) {
        const result = await response.json()
        if (result.success && result.data) {
          set({
            user: {
              id: result.data.id,
              email: result.data.email,
              name: result.data.name,
              avatarUrl: result.data.avatar_url,
              tier: result.data.tier,
            },
            points: result.data.points || 0,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          })
          return true
        }
      }

      // 세션이 없거나 만료됨
      set({
        user: null,
        points: 0,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
      })
      return false
    } catch (error) {
      console.error('Session check error:', error)
      set({
        user: null,
        points: 0,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
      })
      return false
    }
  },
}))
